import React, { useState } from 'react';
import { AppShell, Text, Anchor, Stack, Burger, useMantineTheme, Group, Button } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
  hideNavigation?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, hideNavigation = false }) => {
  const [opened, setOpened] = useState(false);
  const theme = useMantineTheme();
  
  // Use the theme breakpoint properly
  // In Mantine v7, breakpoints are defined in pixels
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  
  // Get auth state from context
  const { isAuthenticated, isAdmin, logout, user } = useAuth();
  const isAuthenticatedNonAdmin = isAuthenticated && !isAdmin;

  return (
    <AppShell
      padding={{ base: 0, sm: 'md' }}
      navbar={hideNavigation ? undefined : {
        width: { base: '100%', sm: 300 },
        breakpoint: 'sm',
        collapsed: { mobile: !opened }
      }}
      header={hideNavigation ? undefined : { height: 60 }}
    >
      {!hideNavigation && (
        <>
          <AppShell.Header p="xs">
            <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {isMobile && (
                  <Burger
                    opened={opened}
                    onClick={() => setOpened((o) => !o)}
                    size="sm"
                    color={theme.colors.gray[6]}
                    mr="xl"
                  />
                )}
                <Anchor 
                  component={Link} 
                  to="/" 
                  style={{ textDecoration: 'none' }}
                  onClick={() => setOpened(false)}
                >
                  <Text size="xl" fw={700}>
                    Menu Interpreter
                  </Text>
                </Anchor>
              </div>
              
              {/* Auth buttons in header */}
              <Group>
                {isAuthenticated ? (
                  <>
                    <Text size="sm" fw={500}>
                      {user?.name}
                    </Text>
                    <Button 
                      variant="subtle" 
                      onClick={() => {
                        logout();
                        setOpened(false);
                      }}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      component={Link} 
                      to="/login" 
                      variant="subtle"
                      onClick={() => setOpened(false)}
                    >
                      Login
                    </Button>
                    <Button 
                      component={Link} 
                      to="/register" 
                      variant="filled"
                      onClick={() => setOpened(false)}
                    >
                      Register
                    </Button>
                  </>
                )}
              </Group>
            </div>
          </AppShell.Header>

          <AppShell.Navbar p="xs">
            <Stack>
              {isAuthenticatedNonAdmin ? (
                <>
                  {/* Non-admin: land on Fast Upload; keep Restaurants one click away */}
                  <Anchor component={Link} to="/upload-fast" onClick={() => setOpened(false)}>
                    Upload Menu by Photo
                  </Anchor>
                  <Anchor component={Link} to="/restaurants" onClick={() => setOpened(false)}>
                    Restaurants
                  </Anchor>
                </>
              ) : (
                <>
                  <Anchor component={Link} to="/upload-fast" onClick={() => setOpened(false)}>
                    Upload Menu by Photo
                  </Anchor>
                  <Anchor component={Link} to="/" onClick={() => setOpened(false)}>
                    Home
                  </Anchor>
                  <Anchor component={Link} to="/restaurants" onClick={() => setOpened(false)}>
                    Restaurants
                  </Anchor>
                  <Anchor component={Link} to="/upload" onClick={() => setOpened(false)}>
                    Upload Menu by Photo
                  </Anchor>
                  <Anchor component={Link} to="/upload-csv" onClick={() => setOpened(false)}>
                    Upload Menu by CSV
                  </Anchor>
                </>
              )}
              
              {/* Show admin section if user is admin */}
              {isAdmin && (
                <>
                  <Text fw={700} mt="md">Admin</Text>
                  <Anchor component={Link} to="/admin/users" onClick={() => setOpened(false)}>
                    Manage Users
                  </Anchor>
                  <Anchor component={Link} to="/query-logs" onClick={() => setOpened(false)}>
                    Query Logs
                  </Anchor>
                </>
              )}
            </Stack>
          </AppShell.Navbar>
        </>
      )}

      <AppShell.Main>
        <div style={{ 
          maxWidth: isMobile ? '100%' : '1200px',
          margin: '0 auto',
          width: '100%'
        }}>
          {children}
        </div>
      </AppShell.Main>
    </AppShell>
  );
};

export default Layout; 